import React, { useEffect, useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { toast } from 'react-toastify';
import { axiosInstance } from '../routes/axiosInstance';
import MarkdownRenderer from '../components/MarkdownRenderer';
import {useBreadcrumbs} from '../contexts/BreadcrumbContext';

/**
 * FAQ Page
 * Dynamically fetches and displays FAQ content from mkdocs documentation
 */
export default function FAQ() {
  usePageTitle('FAQ');
  useBreadcrumbs([{label: 'Help'}, {label: 'FAQ'}], 'Frequently asked questions about the platform');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFAQContent();
  }, []);

  const fetchFAQContent = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/user/docs/?page=faq');
      if (response.data && response.data.data && response.data.data.content) {
        setContent(response.data.data.content);
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      toast.error(`Failed to load FAQ: ${error.message}`);
      // Set fallback content
      setContent(`# FAQ

We're sorry, but the FAQ content couldn't be loaded at this time.

Please try refreshing the page or visit our [documentation site](https://predico-elia.inesctec.pt/docs/faq/) for the latest FAQ.

If this problem persists, please contact support at [predico@inesctec.pt](mailto:predico@inesctec.pt).`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-brand-600 rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading FAQ...</p>
          </div>
        </div>
      ) : (
        <MarkdownRenderer content={content} externalDocsBase="/docs/" />
      )}

      {/* Footer Info */}
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start gap-3">
          <svg
            className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div className="text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-1">Need more help?</p>
            <p>
              This content is automatically synced with our documentation. For
              more detailed information, visit our{' '}
              <a
                href="https://predico-elia.inesctec.pt/docs/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-blue-900 dark:hover:text-blue-200"
              >
                full documentation
              </a>{' '}
              or contact us at{' '}
              <a
                href="mailto:predico@inesctec.pt"
                className="underline hover:text-blue-900 dark:hover:text-blue-200"
              >
                predico@inesctec.pt
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
